/**
 * useHighlights Hook (local-first)
 *
 * Reads from Dexie (server response cached in `meta` via useLocalFirstQuery;
 * offline-created neg-id rows overlaid from db.highlights). Writes go through
 * useLocalMutation so they queue offline and drain via the sync engine.
 *
 * Replaces the previous network-only implementation that broke offline reads
 * (highlight disappeared from view on navigation when offline) — see
 * docs/architecture/offline-pwa-phase-1.md §3.5 + FIX iter 11 in the review.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { liveQuery } from 'dexie';
import { useLocalFirstQuery } from './useLocalFirstQuery';
import { HIGHLIGHTS_TTL_MS } from '../db/freshness';
import { db } from '../db';
import { MUTATION_STATUS } from '../services/mutationLog';
import {
  getArticleHighlightsConditional,
  deleteAllHighlights as deleteAllHighlightsAPI,
  checkHighlightDetachment,
  reattachHighlights as reattachHighlightsAPI,
} from '../services/highlights';
import { findTextPosition } from '../utils/highlightTextMatching';
import useLocalMutation from './useLocalMutation';

/**
 * Calculate SHA-256 hash for summary version tracking
 */
async function calculateSummaryHash(text) {
  const input = text || '';
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback for HTTP contexts (LAN testing). Not cryptographic; sufficient for version tracking.
  const parts = [];
  for (let seed = 0; parts.length < 8; seed++) {
    let h = 5381;
    h = ((h << 5) + h + seed * 7919) >>> 0;
    for (let i = 0; i < input.length; i++) {
      h = ((h << 5) + h + input.charCodeAt(i)) >>> 0;
    }
    h = ((h << 5) + h + seed) >>> 0;
    parts.push(h.toString(16).padStart(8, '0'));
  }
  return parts.join('');
}

function extractContext(fullText, start, end, contextLength = 50) {
  const prefix = fullText.substring(Math.max(0, start - contextLength), start);
  const suffix = fullText.substring(end, Math.min(fullText.length, end + contextLength));
  return { prefix, suffix };
}

export function useHighlights(articleId, summaryText) {
  const [localHighlights, setLocalHighlights] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState(() => new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [detachmentInfo, setDetachmentInfo] = useState(null);
  const [error, setError] = useState(null);
  const { mutate } = useLocalMutation();

  // Reason: hook rules require a stable cacheKey. When articleId is absent,
  // use a sentinel that can't collide with real articles; fetchFn short-circuits.
  const cacheKey = articleId
    ? `highlights:article:${articleId}`
    : 'highlights:article:none';

  const fetchFn = useCallback(async ({ etag }) => {
    if (!articleId) {
      return { status: 200, etag: null, payload: { highlights: [], total: 0, article_id: null } };
    }
    return getArticleHighlightsConditional({ articleId, includeDeleted: false, type: 'all', etag });
  }, [articleId]);

  const { data: serverPayload, loading: isLoading, error: queryError, refresh } = useLocalFirstQuery({
    table: 'meta',
    key: cacheKey,
    ttl: HIGHLIGHTS_TTL_MS,
    fetch: fetchFn,
  });

  // Reason: neg-id rows live in db.highlights (written by applyHighlightCreate
  // as optimistic UI). Reload them whenever articleId changes or after a
  // mutate() call so they appear in the merged list.
  const reloadLocalHighlights = useCallback(async () => {
    if (!articleId) {
      setLocalHighlights([]);
      return;
    }
    try {
      const rows = await db.highlights
        .where('article_id').equals(articleId)
        .filter((h) => h.id < 0)
        .toArray();
      setLocalHighlights(rows);
    } catch (err) {
      console.warn('Failed to load local highlights:', err);
      setLocalHighlights([]);
    }
  }, [articleId]);

  // Reason: also reload when serverPayload changes. After a successful drain,
  // applyBatchResult deletes the neg-id row from IDB but our React `localHighlights`
  // state still holds it. When useLocalFirstQuery later revalidates and returns
  // the server's pos-id row, the merge would show both (duplicate) until the
  // next articleId change. Tying reload to serverPayload keeps state in sync.
  useEffect(() => {
    reloadLocalHighlights();
  }, [reloadLocalHighlights, serverPayload]);

  // Reason: when a highlight mutation drains, applyBatchResult deletes the
  // optimistic neg-id row from db.highlights but cannot refresh the server-backed
  // serverPayload this hook merges against. The confirmed highlight then lives
  // only on the server, masked solely by the stale `localHighlights` state — so
  // the next reloadLocalHighlights() (e.g. creating/deleting another highlight)
  // drops it from the merged list. Watch this article's highlight writes; when
  // they finish draining (leave pending/in_flight), pull the canonical server
  // list so the pos-id row is present before the neg-id mask clears.
  const hadPendingHighlightWrite = useRef(false);
  useEffect(() => {
    if (!articleId) return undefined;
    hadPendingHighlightWrite.current = false;
    const sub = liveQuery(() =>
      db.table('mutation_log')
        .where('status').anyOf([MUTATION_STATUS.PENDING, MUTATION_STATUS.IN_FLIGHT])
        .toArray()
    ).subscribe({
      next: (rows) => {
        // highlight_delete payloads carry only highlight_id (no article_id), so
        // they can't be article-scoped — a stray refresh is a cheap conditional GET.
        const pending = rows.some(
          (m) =>
            (m.type === 'highlight_create' && m.payload?.article_id === articleId)
            || m.type === 'highlight_delete'
        );
        if (hadPendingHighlightWrite.current && !pending) {
          refresh();
        }
        hadPendingHighlightWrite.current = pending;
      },
      error: () => {},
    });
    return () => sub.unsubscribe();
  }, [articleId, refresh]);

  // Optimistic-delete set is article-scoped — only reset on article switch.
  useEffect(() => {
    setPendingDeletes(new Set());
  }, [articleId]);

  const checkDetachment = useCallback(async () => {
    if (!articleId) return;
    try {
      const response = await checkHighlightDetachment(articleId);
      setDetachmentInfo(response);
    } catch (err) {
      // Best-effort — informational call, allowed to fail offline.
      console.error('Error checking detachment:', err);
    }
  }, [articleId]);

  useEffect(() => {
    if (articleId) checkDetachment();
  }, [articleId, checkDetachment]);

  // Merge server-side highlights with offline-created neg-id rows, then hide
  // anything optimistically pending delete.
  const highlights = useMemo(() => {
    const server = serverPayload?.highlights || [];
    const all = [...server, ...localHighlights];
    if (pendingDeletes.size === 0) return all;
    return all.filter((h) => !pendingDeletes.has(h.id));
  }, [serverPayload, localHighlights, pendingDeletes]);

  /**
   * Create a new highlight (queues via mutate; appears immediately via neg-id row).
   */
  const createHighlight = useCallback(
    async (selectedText, selectionRange = null, renderedText = null) => {
      if (!articleId || !summaryText || !selectedText) {
        throw new Error('Missing required data for highlight creation');
      }
      const textForPositioning = renderedText || summaryText;
      try {
        setIsCreating(true);
        setError(null);
        const summaryHash = await calculateSummaryHash(summaryText);
        let position = selectionRange;
        if (!position) {
          position = findTextPosition(textForPositioning, selectedText);
          if (!position) {
            throw new Error('Could not find selected text in rendered content');
          }
        }
        const context = extractContext(textForPositioning, position.start, position.end);

        const annotation = {
          '@context': 'http://www.w3.org/ns/anno.jsonld',
          type: 'Annotation',
          body: { type: 'TextualBody', value: selectedText, format: 'text/plain' },
          target: {
            selector: [
              {
                type: 'RangeSelector',
                startSelector: { type: 'XPathSelector', value: '/html/body/div/div/div' },
                endSelector: { type: 'XPathSelector', value: '/html/body/div/div/div' },
              },
              { type: 'TextPositionSelector', start: position.start, end: position.end },
              {
                type: 'TextQuoteSelector',
                exact: selectedText,
                prefix: context.prefix,
                suffix: context.suffix,
              },
            ],
          },
        };

        const highlightData = {
          annotation,
          exact_text: selectedText,
          summary_version: summaryHash,
        };

        const { key } = await mutate({
          type: 'highlight_create',
          payload: { article_id: articleId, ...highlightData },
        });

        // Read back the neg-id row written by applyHighlightCreate, then
        // refresh the overlay so the new row appears in the merged list.
        const localRow = await db.table('highlights').where('pending_key').equals(key).first();
        await reloadLocalHighlights();
        return localRow;
      } catch (err) {
        console.error('Error creating highlight:', err);
        setError(err.message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [articleId, summaryText, mutate, reloadLocalHighlights]
  );

  /**
   * Delete a highlight (optimistic hide + queued mutation).
   * For neg-id rows: applyHighlightDelete drops the row from db.highlights, so
   * the overlay reload removes it. For pos-id rows: the server row isn't in
   * db.highlights so we rely on pendingDeletes to hide it until the next refresh.
   */
  const deleteHighlight = useCallback(
    async (highlightId) => {
      setError(null);
      setPendingDeletes((prev) => new Set([...prev, highlightId]));
      try {
        await mutate({ type: 'highlight_delete', payload: { highlight_id: highlightId } });
        await reloadLocalHighlights();
      } catch (err) {
        // Roll back the optimistic hide on failure.
        setPendingDeletes((prev) => {
          const next = new Set(prev);
          next.delete(highlightId);
          return next;
        });
        console.error('Error deleting highlight:', err);
        setError(err.message);
        throw err;
      }
    },
    [mutate, reloadLocalHighlights]
  );

  /**
   * Delete all highlights for the article (network call; not queued — used by
   * the article-delete flow which already needs server availability).
   */
  const deleteAllHighlights = useCallback(async () => {
    if (!articleId) return;
    try {
      setError(null);
      await deleteAllHighlightsAPI(articleId);
      setPendingDeletes(new Set());
      await reloadLocalHighlights();
      await refresh();
    } catch (err) {
      console.error('Error deleting all highlights:', err);
      setError(err.message);
      throw err;
    }
  }, [articleId, reloadLocalHighlights, refresh]);

  const reattachHighlights = useCallback(
    async (similarityThreshold = 0.8) => {
      if (!articleId) return;
      try {
        setError(null);
        const response = await reattachHighlightsAPI(articleId, similarityThreshold);
        await refresh();
        await checkDetachment();
        return response;
      } catch (err) {
        console.error('Error re-attaching highlights:', err);
        setError(err.message);
        throw err;
      }
    },
    [articleId, refresh, checkDetachment]
  );

  return {
    highlights,
    isLoading,
    isCreating,
    error: error || queryError?.message || null,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    // Back-compat alias for callers that triggered a refetch manually.
    fetchHighlights: refresh,
    reattachHighlights,
    checkDetachment,
  };
}
